import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App, Alert, Form, Input, Modal, Radio, Select } from "antd";
import { api, unwrap, type Member } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { useWarehouses } from "@/lib/useWarehouses";

// Sign-in for the Android warehouse app: an ID and a 6-digit PIN per member,
// typed on a PDA paired to the warehouse (Pengaturan > Gudang > Perangkat
// Android). Staff without an email can be added with only these.

const ID_RULE = /^[a-z0-9][a-z0-9._-]{2,31}$/;

function idRules(t: (k: "pin.idFormat") => string) {
  return [{ required: true, pattern: ID_RULE, transform: (v: string) => v?.trim().toLowerCase(), message: t("pin.idFormat") }];
}

function pinRules(t: (k: "pin.pinFormat") => string) {
  return [{ required: true, pattern: /^[0-9]{6}$/, message: t("pin.pinFormat") }];
}

/** Set a member's ID and a new PIN. */
export function PinModal({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<{ loginId: string; pin: string }>();
  const save = useMutation({
    mutationFn: (v: { loginId: string; pin: string }) =>
      unwrap(api.PUT("/v1/members/{userID}/pin", { params: { path: { userID: member!.userId } }, body: { loginId: v.loginId.trim().toLowerCase(), pin: v.pin } })),
    onSuccess: () => {
      void message.success(t("pin.saved", { name: member?.name ?? "" }));
      void queryClient.invalidateQueries({ queryKey: ["members"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={member !== null}
      title={t("pin.title", { name: member?.name ?? "" })}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("pin.help")} />
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ loginId: member?.loginId ?? "", pin: "" }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="loginId" label={t("pin.id")} extra={t("pin.idHelp")} rules={idRules(t)}>
          <Input autoFocus autoComplete="off" maxLength={32} />
        </Form.Item>
        <Form.Item name="pin" label={member?.hasPin ? t("pin.newPin") : t("pin.pin")} extra={t("pin.pinHelp")} rules={pinRules(t)}>
          <Input.Password inputMode="numeric" maxLength={6} autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

type StaffValues = { name: string; loginId: string; pin: string; role: "warehouse_staff" | "warehouse_manager"; warehouseIds: string[] };

/** A warehouse member without an email: signs in on PDAs with an ID and PIN only. */
export function StaffModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const warehouses = useWarehouses();
  const [form] = Form.useForm<StaffValues>();
  const save = useMutation({
    mutationFn: (v: StaffValues) =>
      unwrap(
        api.POST("/v1/members/staff", {
          body: { name: v.name.trim(), loginId: v.loginId.trim().toLowerCase(), pin: v.pin, role: v.role, warehouseIds: v.warehouseIds ?? [] },
        }),
      ),
    onSuccess: (m) => {
      void message.success(t("pin.staffAdded", { name: m.name }));
      void queryClient.invalidateQueries({ queryKey: ["members"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      title={t("pin.staffTitle")}
      okText={t("pin.staffAdd")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("pin.staffHelp")} />
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ role: "warehouse_staff", warehouseIds: [] }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, message: t("field.required") }]}>
          <Input autoFocus maxLength={120} />
        </Form.Item>
        <Form.Item name="loginId" label={t("pin.id")} extra={t("pin.idHelp")} rules={idRules(t)}>
          <Input autoComplete="off" maxLength={32} />
        </Form.Item>
        <Form.Item name="pin" label={t("pin.pin")} extra={t("pin.pinHelp")} rules={pinRules(t)}>
          <Input.Password inputMode="numeric" maxLength={6} autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="role" label={t("settings.users.role")}>
          <Radio.Group
            options={[
              { value: "warehouse_staff", label: t("role.warehouse_staff") },
              { value: "warehouse_manager", label: t("role.warehouse_manager") },
            ]}
          />
        </Form.Item>
        <Form.Item name="warehouseIds" label={t("settings.users.warehouses")} extra={t("pin.warehousesHelp")}>
          <Select mode="multiple" options={warehouses.active.map((w) => ({ value: w.id, label: w.name }))} placeholder={t("settings.users.allWarehouses")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
